import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { UPDATE_SORTING_LINES } from '../../../../../../../../store/actions/actionsTypes'
import { IMiunData } from '../../../../../../../../store/reducers/miunReducer'
import { RootState } from '../../../../../../../../store/reducers/rootReducer'
import { Miun } from '../../../../../../../modules/API/Miun/miun'
import { ISortingLinePostData } from '../../../../../../../modules/API/Miun/models/fetchData'
import { ISortingLines } from '../../../../../../../modules/API/Miun/models/sortingList'
import { Defaults } from '../../../../../../../modules/API/Defaults/defaults'
import { Product } from '../../../../../../../modules/API/Products/products'
import { IPackagesItem } from '../../../../../../../modules/API/Defaults/models/packages'
import { IQualityItem } from '../../../../../../../modules/API/Defaults/models/quality'
import { ISizeItem } from '../../../../../../../modules/API/Products/models/size'
import { addIDForTheList } from '../../../../../../../generic/utils/addUIDForTheList'
import { handleNotifications } from '../../../../../../../generic/utils/handleNotifications'
import { LINKS } from '../../../../../../../Routs/config'
import useMiunDateRange from '../../../services/hooks/useMiunDateRange'
import useDataFetcher from '../../../../knisot/hooks/useFetchData'

const useMiunLines = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { from, to } = useMiunDateRange()

    const storedData = useSelector(
        (state: RootState): IMiunData => state.miunReducer,
    )
    const selectedSortingItem = storedData.selectedSortingItem

    const packageList: IPackagesItem[] =
        useSelector((state: RootState) => state.defaults.packageList) || []

    const { data: qualityListData } = useDataFetcher<IQualityItem[]>(
        Defaults.getQualityList,
        'q',
    )
    const { data: sizeListData } = useDataFetcher<{ sizes: ISizeItem[] }>(
        Product.getSizes,
        'size',
    )

    const [lines, setLines] = useState<ISortingLines[]>(() =>
        addIDForTheList([...(selectedSortingItem?.Lines ?? [])]),
    )
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false)
    const [selectedLine, setSelectedLine] = useState<ISortingLines | null>(null)

    const handleOpenCreate = useCallback(() => {
        setSelectedLine(null)
        setIsOpenModal(true)
    }, [])

    const handleOpenEdit = useCallback((line: ISortingLines) => {
        setSelectedLine(line)
        setIsOpenModal(true)
    }, [])

    const handleCloseModal = useCallback(() => {
        setIsOpenModal(false)
        setSelectedLine(null)
    }, [])

    const saveLine = useCallback(
        async (body: ISortingLinePostData) => {
            const sortingNum = selectedSortingItem?.Sorting_Num
            if (!sortingNum) return
            try {
                const res: ISortingLines = selectedLine
                    ? await Miun.putSortingLine(sortingNum, selectedLine.SortRow, body)
                    : await Miun.postSortingLine(sortingNum, [body])

                const next = selectedLine
                    ? lines.map((l) =>
                          l.SortRow === selectedLine.SortRow ? { ...res, id: l.id } : l,
                      )
                    : [...lines, ...addIDForTheList([res])]

                setLines(next)
                dispatch({
                    type: UPDATE_SORTING_LINES,
                    payload: next.map(({ id, ...rest }) => rest),
                })
                handleNotifications(selectedLine ? 'השורה עודכנה' : 'השורה נוספה', {
                    type: 'success',
                })
                handleCloseModal()
            } catch (error: any) {
                handleNotifications(error?.response?.data?.error || 'משהו השתבש', {
                    type: 'error',
                })
            }
        },
        [selectedSortingItem, selectedLine, lines, dispatch, handleCloseModal],
    )

    const handleBack = useCallback(() => {
        const sortingDate = selectedSortingItem?.Date_Sorting
        const newTo = sortingDate && +sortingDate > +to ? sortingDate : to
        navigate(`${LINKS.miun.control.mainURL}?from=${from}&to=${newTo}`, {
            replace: true,
        })
    }, [navigate, selectedSortingItem, from, to])

    const qualityList = useMemo(() => qualityListData || [], [qualityListData])
    const sizeList = useMemo(() => sizeListData?.sizes || [], [sizeListData])

    return {
        selectedSortingItem,
        lines,
        packageList,
        qualityList,
        sizeList,
        isOpenModal,
        selectedLine,
        handleOpenCreate,
        handleOpenEdit,
        handleCloseModal,
        saveLine,
        handleBack,
    }
}

export default useMiunLines
